import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import type {
  DropdownGroupProps,
  DropdownLabelProps,
  DropdownSeparatorProps,
  DropdownSize,
} from './Dropdown.types';
import { useDropdownContext } from './DropdownContext';

/**
 * Left padding per row band — matches `MenuListItem`'s `pl-*` so the label
 * text aligns with the rows' labels below it.
 */
const LABEL_PL: Record<DropdownSize, string> = {
  xl: 'pl-3',
  l: 'pl-3',
  m: 'pl-2.5',
  s: 'pl-2',
};

/**
 * Non-interactive section heading (shadcn `DropdownMenuLabel` counterpart).
 * No Figma node exists for it yet — styled as a muted small-caps-free label
 * whose left padding tracks the row band. Cross-platform.
 */
export function DropdownLabel({
  children,
  className,
  textClassName,
}: DropdownLabelProps) {
  const { size } = useDropdownContext('DropdownLabel');

  return (
    <View
      className={cn(
        'shrink-0 flex-row items-center py-1 pr-2.5',
        LABEL_PL[size],
        className,
      )}>
      {typeof children === 'string' ? (
        <Text
          className={cn(
            // secondary-500 — the menu's own muted token (row descriptions).
            // secondary-400 inverts to #525252 in dark, which vanished into
            // the glass panel wherever it sits over a light page (the deck's
            // PDF behind the materials "+" menu's "Recents").
            'text-b3 font-medium text-fg-subtle native:font-mona-medium',
            textClassName,
          )}>
          {children}
        </Text>
      ) : (
        children
      )}
    </View>
  );
}

/**
 * Hairline divider between menu sections (shadcn `DropdownMenuSeparator`
 * counterpart). Sits inside the panel's padding, matching the rows' inset.
 * Cross-platform.
 */
export function DropdownSeparator({ className }: DropdownSeparatorProps) {
  return (
    <View className={cn('h-px shrink-0 self-stretch bg-secondary-200', className)} />
  );
}

/**
 * Structural grouping of related rows (shadcn `DropdownMenuGroup`
 * counterpart). Keeps the panel's 4-px row gap inside the group.
 * Cross-platform.
 */
export function DropdownGroup({ children, className }: DropdownGroupProps) {
  return (
    <View className={cn('flex shrink-0 flex-col gap-1', className)}>{children}</View>
  );
}
